import { normalizeOptionSlide } from "../utils/normalize-option-slide";
import type {
  PresentationGenerationPartial,
  PresentationGenerationResult,
} from "@/shared/services/presentation-generation/types";
import { create } from "zustand";
import type {
  FileToStorydocTarget,
  PagePreview,
} from "./file-to-storydoc.store";

export type GenerationStatus = "idle" | "generating" | "done" | "error";
export type ImportSource = "local" | "drive";
export type ImportEntryPoint =
  | "dashboard"
  | "dashboard-menu"
  | "stories"
  | "unknown";

export type OptionRef =
  | { kind: "original" }
  | { kind: "regeneration"; regenerationId: string };

export type ImportOption = { k: number; title: string; slide: unknown };

export type RowRating = "up" | "down";

export type ImportRow = {
  pageNumber: number;
  thumbnail: string;
  options: ImportOption[] | null;
  sourceSlide: unknown | null;
  selected: number;
  asStatic: boolean;
  // the k of the pick to restore when asStatic goes off
  previousPick: number | null;
  setRef: OptionRef;
  undo: {
    setRef: OptionRef;
    selected: number;
    options: ImportOption[] | null;
  } | null;
  regenerating: { regenerationId: string } | null;
  failure: { code: string; message: string } | null;
  rating: RowRating | null;
  // the pick's k carried over a reload, until options arrive to resolve it
  restoredK: number | null;
  // the undo set is being re-fetched (its options were not cached)
  undoing: boolean;
};

export type ImportRequest = {
  pdfUrl: string;
  pages: number[];
  designSystemId?: string;
  themed?: boolean;
};

// The dialog's upload context at Continue, so Back to pages can restore this
// import's pages after other uploads. Not persisted (File objects).
export type UploadSnapshot = {
  originalFile: File | null;
  pdfFile: File | null;
  pdfUrl: string;
  pages: PagePreview[];
  selectedDesignSystemId: string | null;
  source: ImportSource;
};

export type StartJobPayload = {
  request: ImportRequest;
  selectedDesignSystemId: string | null;
  // The user chose the open deck's own theme — see the note in
  // file-to-storydoc.store.ts. Distinct from a null id, which means "look
  // like the file".
  keepDeckTheme?: boolean;
  // Apply the source's design and insert nothing — see file-to-storydoc.store.
  themeOnly?: boolean;
  title: string;
  fileName: string;
  source: ImportSource;
  entryPoint: ImportEntryPoint;
  // Defaults to the dashboard import; the editor's agent passes "deck".
  target?: FileToStorydocTarget;
  pageThumbnails: string[];
  upload?: UploadSnapshot;
};

export type CreatedStory = {
  _id: string;
  previewUrl?: string;
  contentPointer?: string;
};

export interface IState {
  status: GenerationStatus;
  jobId: string | null;
  // identifies one run so callbacks from a superseded run can be ignored
  runId: string | null;
  // Which runner owns this job: the global manager creates a story for
  // "new-story", the editor's agent inserts into the open deck for "deck".
  // Each ignores the other's jobs.
  target: FileToStorydocTarget;
  title: string;
  fileName: string;
  source: ImportSource;
  entryPoint: ImportEntryPoint;
  request: ImportRequest | null;
  selectedDesignSystemId: string | null;
  keepDeckTheme: boolean;
  themeOnly: boolean;
  designColors: Record<string, string> | null;
  deckTitle: string | null;
  rows: ImportRow[];
  modalOpen: boolean;
  // the completion toast was closed with its X; reset per job
  completeToastDismissed: boolean;
  // the progress toast was closed with its X; the import keeps running
  progressToastDismissed: boolean;
  createdStory: CreatedStory | null;
  errorMsg: string | null;
  startedAt: string | null;
  startedJob: boolean;
  upload: UploadSnapshot | null;
}

export type HydratePayload = Omit<
  IState,
  | "startedJob"
  | "modalOpen"
  | "target"
  | "keepDeckTheme"
  | "themeOnly"
  | "createdStory"
  | "errorMsg"
  | "completeToastDismissed"
  | "progressToastDismissed"
  | "upload"
>;

export interface IActions {
  startJob: (p: StartJobPayload) => void;
  markStarted: () => void;
  setJobId: (jobId: string) => void;
  setEntryPoint: (entryPoint: ImportEntryPoint) => void;
  setModalOpen: (open: boolean) => void;
  setCompleteToastDismissed: (dismissed: boolean) => void;
  setProgressToastDismissed: (dismissed: boolean) => void;
  setStatus: (status: GenerationStatus) => void;
  applyPartial: (partial: PresentationGenerationPartial) => void;
  applyResult: (result: PresentationGenerationResult) => void;
  selectOption: (rowIndex: number, k: number) => void;
  setStatic: (rowIndex: number, on: boolean) => void;
  startRegeneration: (rowIndex: number, regenerationId: string) => void;
  applyRegeneration: (
    rowIndex: number,
    regenerationId: string,
    options: ImportOption[],
  ) => void;
  failRegeneration: (rowIndex: number, regenerationId: string | null) => void;
  applyRegenerationSet: (
    rowIndex: number,
    regenerationId: string,
    options: ImportOption[],
  ) => void;
  restoreOriginalSet: (
    rowIndex: number,
    options: ImportOption[] | null,
  ) => void;
  setUndoing: (rowIndex: number, undoing: boolean) => void;
  undo: (rowIndex: number, fetched?: ImportOption[]) => void;
  setRating: (rowIndex: number, rating: RowRating) => void;
  setCreatedStory: (c: CreatedStory | null) => void;
  setError: (msg: string) => void;
  hydrate: (p: HydratePayload) => void;
  reset: () => void;
}

export type ImportStoreState = IState & IActions;

const initState: IState = {
  status: "idle",
  jobId: null,
  runId: null,
  target: "new-story",
  title: "",
  fileName: "",
  source: "local",
  entryPoint: "unknown",
  request: null,
  selectedDesignSystemId: null,
  keepDeckTheme: false,
  themeOnly: false,
  designColors: null,
  deckTitle: null,
  rows: [],
  modalOpen: false,
  completeToastDismissed: false,
  progressToastDismissed: false,
  createdStory: null,
  errorMsg: null,
  startedAt: null,
  startedJob: false,
  upload: null,
};

const pendingRow = (pageNumber: number, thumbnail: string): ImportRow => ({
  pageNumber,
  thumbnail,
  options: null,
  sourceSlide: null,
  selected: 0,
  asStatic: false,
  previousPick: null,
  setRef: { kind: "original" },
  undo: null,
  regenerating: null,
  failure: null,
  rating: null,
  restoredK: null,
  undoing: false,
});

const newRunId = () =>
  Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

export const MAIN_PICK_TITLE = "Our pick";
export const FILL_FAILED_CODE = "GENERATION_FAILED";
export const FILL_FAILED_MESSAGE = "We couldn't convert this slide.";
export const REGENERATE_FAILED_CODE = "REGENERATE_FAILED";
export const REGENERATE_FAILED_MESSAGE = "Couldn't regenerate this slide.";

type PartialEntry = PresentationGenerationPartial["slides"][string];

// The engine's main slide is option 0; its alternates keep their 1-based k.
const optionsFrom = (
  main: unknown,
  mainTitle: string | null | undefined,
  alts: Array<{ k: number; title: string; slide: unknown }>,
): ImportOption[] => [
  {
    k: 0,
    title: mainTitle?.trim() || MAIN_PICK_TITLE,
    slide: normalizeOptionSlide(main),
  },
  ...alts
    .filter((a) => a.slide != null)
    .sort((a, b) => a.k - b.k)
    .map((a) => ({
      k: a.k,
      title: a.title,
      slide: normalizeOptionSlide(a.slide),
    })),
];

export const optionsFromPartialEntry = (
  e: PartialEntry | undefined,
): ImportOption[] | null =>
  e?.final && !e.failed && e.slide != null
    ? optionsFrom(e.slide, e.layoutTitle, e.options ?? [])
    : null;

export const optionsFromResult = (
  result: PresentationGenerationResult,
  i: number,
): ImportOption[] | null => {
  const main = result.slides_v1[i];
  if (main == null || result.slides_failed?.[i]) return null;
  const titles = result.alternativeTitles?.[i] ?? {};
  const alts = Object.keys(titles).map((key) => ({
    k: Number(key),
    title: titles[key],
    slide: result.slides_alt?.[Number(key) - 1]?.[i] ?? null,
  }));
  return optionsFrom(main, result.layoutTitles?.[i], alts);
};

const withOptions = (
  row: ImportRow,
  options: ImportOption[],
  sourceSlide: unknown | null,
): ImportRow => {
  // a prior selection is kept, resolved by option k rather than index
  const previousK =
    row.options?.[row.selected]?.k ?? row.restoredK ?? undefined;
  const kept =
    previousK == null ? -1 : options.findIndex((o) => o.k === previousK);
  return {
    ...row,
    options,
    sourceSlide,
    selected: kept >= 0 ? kept : 0,
    // polls repeat final pages; a failed regenerate on a built row stays visible
    failure:
      (row.options != null || row.restoredK != null) &&
      row.failure?.code === REGENERATE_FAILED_CODE
        ? row.failure
        : null,
    restoredK: null,
  };
};

// a row already showing a failure or a retry keeps it; polls repeat the page
const failedRow = (row: ImportRow, sourceSlide: unknown | null): ImportRow =>
  row.failure != null || row.regenerating != null
    ? { ...row, sourceSlide }
    : {
        ...row,
        options: null,
        sourceSlide,
        selected: 0,
        restoredK: null,
        failure: { code: FILL_FAILED_CODE, message: FILL_FAILED_MESSAGE },
      };

const unsetStatic = (r: ImportRow): ImportRow => {
  const base = { ...r, asStatic: false, previousPick: null };
  if (r.previousPick == null) return base;
  if (!r.options) return { ...base, restoredK: r.previousPick };
  const kept = r.options.findIndex((o) => o.k === r.previousPick);
  return kept >= 0 ? { ...base, selected: kept } : base;
};

const mapRow = (
  rows: ImportRow[],
  i: number,
  fn: (r: ImportRow) => ImportRow,
) => rows.map((r, idx) => (idx === i ? fn(r) : r));

export const createImportStore = () =>
  create<ImportStoreState>((set) => ({
    ...initState,
    startJob: ({
      request,
      selectedDesignSystemId,
      keepDeckTheme,
      themeOnly,
      title,
      fileName,
      source,
      entryPoint,
      target,
      pageThumbnails,
      upload,
    }) =>
      set({
        ...initState,
        status: "generating",
        runId: newRunId(),
        target: target ?? "new-story",
        request,
        selectedDesignSystemId,
        keepDeckTheme: !!keepDeckTheme,
        themeOnly: !!themeOnly,
        title,
        fileName,
        source,
        entryPoint,
        upload: upload ?? null,
        startedAt: new Date().toISOString(),
        rows: request.pages.map((pn, i) =>
          pendingRow(pn, pageThumbnails[i] ?? ""),
        ),
      }),
    markStarted: () => set({ startedJob: true }),
    setJobId: (jobId) => set({ jobId }),
    setEntryPoint: (entryPoint) => set({ entryPoint }),
    setModalOpen: (modalOpen) => set({ modalOpen }),
    setCompleteToastDismissed: (completeToastDismissed) =>
      set({ completeToastDismissed }),
    setProgressToastDismissed: (progressToastDismissed) =>
      set({ progressToastDismissed }),
    setStatus: (status) => set({ status }),
    applyPartial: (partial) =>
      set((s) =>
        s.rows.length === 0
          ? {}
          : {
              designColors: partial.designColors ?? s.designColors,
              rows: s.rows.map((row) => {
                const e = partial.slides[String(row.pageNumber)];
                if (!e?.final) return row;
                const sourceSlide = e.sourceSlide ?? row.sourceSlide;
                if (row.setRef.kind === "regeneration")
                  return { ...row, sourceSlide };
                if (e.failed) return failedRow(row, sourceSlide);
                const options = optionsFromPartialEntry(e);
                return options ? withOptions(row, options, sourceSlide) : row;
              }),
            },
      ),
    applyResult: (result) =>
      set((s) =>
        s.rows.length === 0
          ? {}
          : {
              status: "done",
              deckTitle: result.deckTitle?.trim() || s.deckTitle,
              designColors: result.pdfDesignColors ?? s.designColors,
              rows: s.rows.map((row, i) => {
                const sourceSlide =
                  result.slides_source?.[i] ?? row.sourceSlide;
                if (row.setRef.kind === "regeneration")
                  return { ...row, sourceSlide };
                const options = optionsFromResult(result, i);
                return options
                  ? withOptions(row, options, sourceSlide)
                  : failedRow(row, sourceSlide);
              }),
            },
      ),
    selectOption: (rowIndex, k) =>
      set((s) => ({
        rows: mapRow(s.rows, rowIndex, (r) =>
          r.options?.some((o) => o.k === k)
            ? { ...r, selected: r.options.findIndex((o) => o.k === k) }
            : r,
        ),
      })),
    setStatic: (rowIndex, on) =>
      set((s) => ({
        rows: mapRow(s.rows, rowIndex, (r) =>
          on
            ? {
                ...r,
                asStatic: true,
                previousPick: r.options?.[r.selected]?.k ?? r.restoredK,
              }
            : unsetStatic(r),
        ),
      })),
    startRegeneration: (rowIndex, regenerationId) =>
      set((s) => ({
        rows: mapRow(s.rows, rowIndex, (r) => ({
          ...r,
          regenerating: { regenerationId },
          failure: null,
        })),
      })),
    applyRegeneration: (rowIndex, regenerationId, options) =>
      set((s) => ({
        rows: mapRow(s.rows, rowIndex, (r) => {
          if (r.regenerating?.regenerationId !== regenerationId) return r;
          if (options.length === 0) return r;
          const hadSet =
            r.options != null ||
            r.restoredK != null ||
            r.setRef.kind === "regeneration";
          return {
            ...r,
            undo: hadSet
              ? { setRef: r.setRef, selected: r.selected, options: r.options }
              : null,
            setRef: { kind: "regeneration", regenerationId },
            options,
            selected: 0,
            previousPick: r.asStatic ? options[0].k : r.previousPick,
            regenerating: null,
            failure: null,
            restoredK: null,
            undoing: false,
          };
        }),
      })),
    failRegeneration: (rowIndex, regenerationId) =>
      set((s) => ({
        rows: mapRow(s.rows, rowIndex, (r) => {
          const running = r.regenerating?.regenerationId ?? null;
          if (
            regenerationId == null
              ? running != null
              : running !== regenerationId
          )
            return r;
          return {
            ...r,
            regenerating: null,
            failure: {
              code: REGENERATE_FAILED_CODE,
              message: REGENERATE_FAILED_MESSAGE,
            },
          };
        }),
      })),
    applyRegenerationSet: (rowIndex, regenerationId, options) =>
      set((s) => ({
        rows: mapRow(s.rows, rowIndex, (r) =>
          r.setRef.kind === "regeneration" &&
          r.setRef.regenerationId === regenerationId &&
          options.length > 0
            ? withOptions(r, options, r.sourceSlide)
            : r,
        ),
      })),
    restoreOriginalSet: (rowIndex, options) =>
      set((s) => ({
        rows: mapRow(s.rows, rowIndex, (r) => {
          if (r.setRef.kind !== "regeneration") return r;
          const base: ImportRow = {
            ...r,
            setRef: { kind: "original" },
            undo: null,
            restoredK: null,
            previousPick: r.asStatic ? (options?.[0]?.k ?? null) : null,
          };
          return options?.length
            ? withOptions(base, options, r.sourceSlide)
            : failedRow({ ...base, failure: null }, r.sourceSlide);
        }),
      })),
    setUndoing: (rowIndex, undoing) =>
      set((s) => ({
        rows: mapRow(s.rows, rowIndex, (r) => ({ ...r, undoing })),
      })),
    undo: (rowIndex, fetched) =>
      set((s) => ({
        rows: mapRow(s.rows, rowIndex, (r) => {
          const options = fetched ?? r.undo?.options ?? null;
          if (!r.undo || !options?.length || r.regenerating) return r;
          const selected = Math.min(r.undo.selected, options.length - 1);
          return {
            ...r,
            setRef: r.undo.setRef,
            options,
            selected,
            previousPick: r.asStatic ? options[selected].k : r.previousPick,
            undo: null,
            undoing: false,
            failure: null,
            restoredK: null,
          };
        }),
      })),
    setRating: (rowIndex, rating) =>
      set((s) => ({
        rows: mapRow(s.rows, rowIndex, (r) => ({
          ...r,
          rating: r.rating === rating ? null : rating,
        })),
      })),
    setCreatedStory: (createdStory) => set({ createdStory }),
    setError: (errorMsg) => set({ status: "error", errorMsg }),
    hydrate: (p) =>
      set({
        ...initState,
        ...p,
        runId: p.runId ?? newRunId(),
        startedJob: false,
        modalOpen: false,
        completeToastDismissed: false,
        progressToastDismissed: false,
        createdStory: null,
        errorMsg: null,
      }),
    reset: () => set({ ...initState }),
  }));

export type ImportStore = ReturnType<typeof createImportStore>;

// The editor agent's deck import: one at a time, outside the registry.
export const deckImportStore = createImportStore();

const rowIsFinal = (r: ImportRow) =>
  r.asStatic ? r.sourceSlide != null : r.options != null;

export const selectAllFinal = (rows: ImportRow[]) =>
  rows.length > 0 && rows.every(rowIsFinal);

// every row has resolved: options, a failure, or a static source slide
export const selectAllSettled = (rows: ImportRow[]) =>
  rows.length > 0 &&
  rows.every(
    (r) =>
      r.options != null ||
      r.failure != null ||
      (r.asStatic && r.sourceSlide != null),
  );

export const selectFinalCount = (rows: ImportRow[]) =>
  rows.filter(rowIsFinal).length;

// settled rows that still block Accept: a failed fill the user has to retry or make static
export const selectAttentionCount = (rows: ImportRow[]) =>
  rows.filter((r) => !rowIsFinal(r) && r.failure != null && !r.regenerating)
    .length;

export const selectBuiltCount = (rows: ImportRow[]) =>
  rows.filter((r) => r.options != null).length;

export const selectChangedCount = (rows: ImportRow[]) =>
  rows.filter((r) => r.asStatic || r.selected !== 0).length;

export const selectIsImportPending = (
  s: Pick<IState, "status" | "createdStory">,
) =>
  s.status === "generating" || (s.status === "done" && s.createdStory == null);

export const composeChosenSlides = (rows: ImportRow[]): unknown[] =>
  rows.map((r) => {
    if (r.asStatic && r.sourceSlide != null) return r.sourceSlide;
    return r.options?.[r.selected]?.slide ?? r.options?.[0]?.slide ?? null;
  });
